import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AnalysisResponseDto } from '@inference.engine/api-client/web';

import { useTranslation } from 'react-i18next';

import { ANALYSIS_QUERY_KEYS } from '@/entities/analysis';
import { generateAudienceDrafts } from '@/features/analysis/api/analysis.api';
import { notificationService } from '@/lib/services/notification.service';
import { readAudienceDrafts, toAudienceDraftCards } from './audienceDrafts';
import type { AudienceDraftCard } from './audienceDrafts';

/** How often the report is re-read while its audiences are being built. */
const POLL_INTERVAL_MS = 15_000;

/** Matches the server's claim lease, so a run nobody is finishing can be taken over. */
const STALE_CLAIM_MS = 10 * 60 * 1000;

interface UseAudienceDraftsResult {
  cards: AudienceDraftCard[];
  /** True while any audience is still being built. */
  isBuilding: boolean;
  /** True when the run finished and left at least one audience unbuilt. */
  hasFailures: boolean;
  retry: () => void;
  isRetrying: boolean;
}

/**
 * Builds a report's audiences on demand, and keeps the report fresh while they arrive. Which
 * audiences those are - the ones IQ suggested, or the ones read out of the documents the
 * report was created with - is whichever block the report carries; the two are built and
 * watched identically, and only the route differs.
 *
 * Nothing is built until this section is opened, so a report nobody reads costs nothing. The
 * first open asks once - the request is what claims the report, and the server refuses a
 * second run, so mounting twice cannot pay twice.
 */
export const useAudienceDrafts = (
  analysis: AnalysisResponseDto,
  /** A headless render for export: it reads the report and must not start work or poll. */
  { exporting = false }: { exporting?: boolean } = {},
): UseAudienceDraftsResult => {
  const { t } = useTranslation('analysis');
  const queryClient = useQueryClient();
  const read = readAudienceDrafts(analysis);
  const drafts = read?.drafts;
  const cards = toAudienceDraftCards(analysis);

  // A claim older than the server's lease is a run nobody is finishing - a pod that died
  // mid-way leaves the report saying `processing` with nothing left to write it. Treating it
  // as over is what lets the reader ask again, which re-claims the expired lease; without it
  // the section watches a dead run forever and never offers the retry.
  const startedAt = drafts?.startedAt ? Date.parse(drafts.startedAt) : undefined;
  const claimIsStale =
    drafts?.status === 'processing' &&
    startedAt !== undefined &&
    Date.now() - startedAt > STALE_CLAIM_MS;

  // The run's own status is included on purpose: a retry starts from audiences that say
  // `failed`, and reading only the audiences would call that run idle and never watch it.
  const isBuilding =
    !claimIsStale &&
    (drafts?.status === 'processing' ||
      cards.some((card) => card.status === 'pending' || card.status === 'processing'));
  const hasFailures = claimIsStale || cards.some((card) => card.status === 'failed');

  const refreshReport = () =>
    queryClient.invalidateQueries({ queryKey: ANALYSIS_QUERY_KEYS.detail(analysis.id) });

  const envelope = read?.envelope;
  const generate = useMutation({
    // A report with no drafts has nothing to ask for: it predates the feature, or its
    // audiences were all built already.
    mutationFn: async () => {
      if (envelope) await generateAudienceDrafts(analysis.id, envelope);
    },
    onSettled: refreshReport,
  });

  // The report is the source of truth for progress, so it is re-read rather than the
  // enrichment being subscribed to. Stops as soon as nothing is left building.
  useEffect(() => {
    if (exporting || !isBuilding) return;

    const interval = setInterval(refreshReport, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshReport is stable per analysis id
  }, [isBuilding, analysis.id, exporting]);

  // Announced on the way out of building, not on finding it already done: a report opened after
  // the run finished would otherwise greet the reader with news about work they never saw
  // start. Only reaches someone still on the page - the run outlives it, and telling them
  // after they leave needs what the analysis itself uses to notify.
  const wasBuilding = useRef(false);
  useEffect(() => {
    if (exporting) return;
    if (isBuilding) {
      wasBuilding.current = true;
      return;
    }
    if (!wasBuilding.current) return;
    wasBuilding.current = false;

    if (hasFailures) {
      notificationService.showError(t('analysisResult.suggestedAudiences.someAudiencesCouldNot'));
      return;
    }
    notificationService.showSuccess(t('analysisResult.suggestedAudiences.audiencesAreReady'));
  }, [isBuilding, hasFailures, exporting, t]);

  // Asked once per report, and only for audiences nobody has tried yet - the run's own status
  // says that. A failed audience waits for the retry instead, since opening the section again
  // should not quietly spend on work that already failed once.
  //
  // `generate.mutate` stays out of the dependency list on purpose: including it re-fires on
  // every render that produces a new mutation object.
  const neverRun = drafts?.status === 'pending';
  const askedFor = useRef<string | null>(null);
  useEffect(() => {
    // An export is a read: it renders whatever the report holds and pays for nothing.
    if (exporting || !neverRun || askedFor.current === analysis.id) return;

    askedFor.current = analysis.id;
    generate.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [analysis.id, neverRun, exporting]);

  return {
    cards,
    isBuilding,
    hasFailures,
    retry: () => generate.mutate(),
    isRetrying: generate.isPending,
  };
};
